import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Slider
} from "@material-ui/core";
import { utcParse } from "d3-time-format";
import { scaleLinear, scaleTime } from "d3-scale";
import { extent } from "d3-array";
import moment from "moment";
import { createMuiTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";
import Chart from "./Chart";

const margin = { top: 10, right: 20, bottom: 20, left: 25 };

const height = 400 - margin.bottom - margin.top;
const width = 550 - margin.left - margin.right;
const parseTime = utcParse("%Y-%m-%dT%H:%M:%S.%LZ");

const depthStyle = { color: "#B60C0F", marginLeft: "25px" };
const timeStyle = { color: "grey", fontSize: "75%" };

const ChartBody = props => {
  const depthBoundsTheme = {
    color: "#AAAAFF",
    fill: "#AAAAFF",
    opacity: 0.3
  };
  const muiTheme = createMuiTheme({
    overrides: {
      MuiSlider: {
        thumb: {
          color: depthBoundsTheme.color
        },
        track: {
          color: depthBoundsTheme.color,
          opacity: depthBoundsTheme.opacity
        },
        rail: {
          color: "black",
          opacity: depthBoundsTheme.opacity
        }
      }
    }
  });

  let boundingBoxWidth = width;
  let boundingBoxMargin = 0;
  if (props.soloChartData) {
    const extentInst = extent(props.soloChartData, d =>
      parseTime(d.measurementDt)
    );
    const xScale = scaleTime()
      .domain(extentInst)
      .range([0, width]);
    boundingBoxWidth =
      xScale(moment(props.soloChartModalCast.endDt).toDate()) -
      xScale(moment(props.soloChartModalCast.startDt).toDate());
    boundingBoxMargin =
      xScale(moment(props.soloChartModalCast.startDt).toDate()) + 25;
  }
  return (
    <div>
      <ThemeProvider theme={muiTheme}>
        <Typography style={{ color: depthStyle.color }}>
          {`Bottom depth: ${Math.round(props.modalBottomDepth * 10) / 10} m `}{" "}
          <span style={timeStyle}>(detected at dashed line)</span>
        </Typography>
        <Typography variant="body1">
          Bottom time:{" "}
          {moment(props.modalBottomDatetime).format("dddd, MMMM Do YYYY,")}{" "}
          <span style={{ fontWeight: "bold" }}>
            {moment(props.modalBottomDatetime).format(" h:mm:ss a")}
          </span>
        </Typography>
        {props.soloChartModalOpen && !props.soloChartDataFetching ? (
          <div>
            <Typography variant="subtitle2" style={{ textAlign: "center" }}>
              Select time bounds for detecting bottom depth of cast
            </Typography>
            <Box
              sx={{ width: boundingBoxWidth, marginLeft: boundingBoxMargin }}
            >
              <Slider
                sx={{ color: "#873423" }}
                value={props.depthBounds}
                min={props.defaultDatetimeMinEpoch}
                max={props.defaultDatetimeMaxEpoch}
                onChange={props.handleModalDepthBounds}
                valueLabelDisplay="off"
              />
            </Box>
          </div>
        ) : (
          <LinearProgress />
        )}

        {props.soloChartModalOpen && !props.soloChartDataFetching ? (
          <Chart
            height={height}
            width={width}
            margin={margin}
            data={props.soloChartData}
            x={scaleTime()}
            y={scaleLinear()}
            getX={d => parseTime(d.measurementDt)}
            getY={d => d.depth}
            rectData={props.soloChartModalCast}
            rectStartAccessor={c => parseTime(c.startDt)}
            rectEndAccessor={c => parseTime(c.endDt)}
            depthBounds={props.depthBounds}
            depthBoundsTheme={depthBoundsTheme}
            bottomDepth={-props.modalBottomDepth}
            bottomDatetime={props.modalBottomDatetime}
          />
        ) : (
          <LinearProgress />
        )}
      </ThemeProvider>
    </div>
  );
};

const ChartModal = props => {
  let chartBodyProps = { ...props };
  let updateStoreDepthBounds = () => {};
  let handleModalDepthBounds = () => {};
  if (props.soloChartModalOpen && !props.soloChartDataFetching) {
    const [defaultDatetimeMinEpoch, defaultDatetimeMaxEpoch] = [
      props.soloChartModalCast.startDt,
      props.soloChartModalCast.endDt
    ].map(x => moment(x).valueOf());
    const [modalState, setModalState] = useState({
      modalBottomDatetime: props.soloChartModalCast.bottomDt,
      modalBottomDepth: props.soloChartModalCast.bottomDepth,
      depthBounds: [defaultDatetimeMinEpoch, defaultDatetimeMaxEpoch] // Use epoch for slider because requires int type
    });
    updateStoreDepthBounds = () =>
      props.onChangeChartDepthBounds({
        ...modalState,
        soloCastPk: props.soloChartModalCast.soloCastPk,
        soloFilePk: props.soloChartModalCast.soloFilePk
      });

    handleModalDepthBounds = (event, newWindow) => {
      const castData = props.soloChartData;
      const castSection = castData.filter(
        x =>
          moment(x.measurementDt).valueOf() > newWindow[0] &&
          moment(x.measurementDt).valueOf() < newWindow[1]
      );
      const newBottomDepth = -Math.min(...castSection.map(x => x.depth));
      setModalState({
        ...modalState,
        ...{
          modalBottomDepth: newBottomDepth,
          modalBottomDatetime: castData.find(x => x.depth === -newBottomDepth)
            .measurementDt,
          depthBounds: newWindow
        }
      });
    };
    chartBodyProps = {
      ...props,
      ...modalState,
      ...{
        defaultDatetimeMinEpoch,
        defaultDatetimeMaxEpoch,
        handleModalDepthBounds
      }
    };
    chartBodyProps = {
      ...props,
      ...modalState,
      ...{
        defaultDatetimeMinEpoch,
        defaultDatetimeMaxEpoch,
        handleModalDepthBounds,
        updateStoreDepthBounds
      }
    };
  }
  return (
    <Dialog open={props.soloChartModalOpen}>
      <DialogTitle>Cast Detection Results</DialogTitle>
      <DialogContent>
        {props.soloChartModalOpen && !props.soloChartDataFetching ? (
          <ChartBody {...chartBodyProps} />
        ) : (
          <LinearProgress />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={props.onChartModalClose}>
          Cancel
        </Button>
        {props.soloChartModalOpen && !props.soloChartDataFetching ? (
          <Button variant="text" onClick={updateStoreDepthBounds}>
            Accept
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

const dataShape = PropTypes.shape({
  measurementDt: PropTypes.string,
  depth: PropTypes.number
});

ChartModal.propTypes = {
  onChartModalClose: PropTypes.func.isRequired,
  onChangeChartDepthBounds: PropTypes.func.isRequired,
  soloChartModalOpen: PropTypes.bool,
  soloChartModalCast: PropTypes.shape({
    soloFilePk: PropTypes.number,
    soloCastPk: PropTypes.number,
    startDt: PropTypes.string,
    bottomDt: PropTypes.string,
    endDt: PropTypes.string,
    deviceSn: PropTypes.string,
    bottomDepth: PropTypes.number
  }),
  soloChartDataFetching: PropTypes.bool,
  soloChartData: PropTypes.arrayOf(dataShape)
};

ChartModal.defaultProps = {
  soloChartModalOpen: true,
  soloChartModalCast: {},
  soloChartDataFetching: true,
  soloChartData: []
};

ChartBody.propTypes = {
  soloChartModalCast: ChartModal.propTypes.soloChartModalCast,
  soloChartData: ChartModal.propTypes.soloChartData,
  soloChartModalOpen: ChartModal.propTypes.soloChartModalOpen,
  soloChartDataFetching: ChartModal.propTypes.soloChartDataFetching,
  modalBottomDatetime: PropTypes.string.isRequired, // iso timestring for d3 to parse
  modalBottomDepth: PropTypes.number.isRequired,
  depthBounds: PropTypes.arrayOf(PropTypes.number).isRequired,
  defaultDatetimeMinEpoch: PropTypes.number.isRequired, // epoch time for slider
  defaultDatetimeMaxEpoch: PropTypes.number.isRequired,
  handleModalDepthBounds: PropTypes.func.isRequired
};

ChartBody.defaultProps = {
  ...ChartModal.defaultProps
};

export default ChartModal;
